import Constants from "./constants";

class BSHelper {
	/**
	 * Checks if the current page direction is right-to-left ('rtl').
	 *
	 * @return {boolean} `true` if the direction is 'rtl', `false` otherwise.
	 */
	isPageRTL() {
		return document.dir === "rtl";
	}

	/**
	 * Checks if the current page is in English.
	 *
	 * @return {boolean} `true` if the language is English, `false` otherwise.
	 */
	isEnglishLanguage() {
		return document.documentElement.lang === "en";
	}

	/**
	 * Checks if the current page is in Arabic.
	 *
	 * @return {boolean} `true` if the language is Arabic, `false` otherwise.
	 */
	isArabicLanguage() {
		return document.documentElement.lang === "ar";
	}

	/**
	 * Formats a given date into a string representation based on the specified locale.
	 *
	 * @param {Date} date - The date to be formatted.
	 * @param {string} [locale='en-US'] - The locale to use for formatting. Defaults to 'en-US'.
	 * @return {string} The formatted date string.
	 */
	formatDate(date, locale = "en-US", isWeekDayLong = false) {
		const dateOptions = {
			year: "numeric",
			month: "short",
			day: "numeric",
		};
		if (isWeekDayLong) {
			dateOptions.weekday = "long";
		}

		return new Intl.DateTimeFormat(locale, dateOptions).format(date);
	}

	/**
	 * Calculates the number of days between the posted date and current date
	 *
	 * @param {string} datePosted - ISO 8601 formatted date string (e.g. "2024-11-17T10:32:34.6248648+00:00")
	 * @return {number} Number of days since posting
	 */
	getPostedDays(datePosted) {
		const posted = new Date(datePosted);
		const current = new Date();

		// Calculate difference in milliseconds and convert to days
		const diffTime = current - posted;
		const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

		// Return absolute value to handle future dates
		return Math.abs(diffDays);
	}

	getReadingTime(text) {
		if (text !== undefined || text !== null || text !== "") {
			const wpm = 225; // Average words per minute
			const words = text.trim().split(/\s+/).length;
			const readingTime = Math.ceil(words / wpm);
			return readingTime;
		}
	}

	/**
	 * Trims a multibyte string to a specified width.
	 *
	 * @param {string} str - The input string to be trimmed.
	 * @param {string} start - The starting character of the trim.
	 * @param {number} length - The desired width of the trimmed string.
	 * @param {string} truncate_string - The string to append when truncating.
	 * @return {string} The trimmed string.
	 */
	mb_strimwidth(str, start, length, truncateString) {
		const charCount = this.getCharCount(str);
		const startCharIndex = this.getCharIndex(str, start);
		const endCharIndex = startCharIndex + length;

		if (endCharIndex > charCount) {
			return truncateString + "...";
		}

		return str.substring(startCharIndex, endCharIndex);
	}

	/**
	 * Returns the character count of a given string, considering multibyte characters.
	 *
	 * @param {string} str - The input string to count characters from.
	 * @return {number} The total character count of the input string.
	 */
	getCharCount(str) {
		let count = 0;
		for (let i = 0; i < str.length; i++) {
			const charCode = str.charCodeAt(i);
			if (charCode > 255) {
				count += 2;
			} else {
				count++;
			}
		}
		return count;
	}

	/**
	 * Returns the index of the first occurrence of a specified character in a string.
	 *
	 * @param {string} str - The input string to search for the character.
	 * @param {string} char - The character to search for in the string.
	 * @return {number} The index of the first occurrence of the character, or the last index if not found.
	 */
	getCharIndex(str, char) {
		let index = 0;
		for (let i = 0; i < str.length; i++) {
			if (str[i] === char) {
				index = i;
				break;
			}
		}
		return index;
	}
	/**
	 * Truncates a string to a specified length and adds an ellipsis if needed.
	 *
	 * @param {string} str - The input string to truncate
	 * @param {number} n - The maximum length of the truncated string
	 * @returns {string} The truncated string with ellipsis if length exceeded, otherwise original string
	 */
	truncate(str, n) {
		return str.length > n ? str.slice(0, n - 1) + "..." : str;
	}

	getApiRootUrl() {
		const envType = window.bseitEditorVars?.envType || "dev";
		const forceStagingDomains = [
			"cos.4.161.35.151.nip.io", // Add domains you want to force to staging
			"sajaya.4.161.35.151.nip.io",
			"fmdp.4.161.35.151.nip.io",
			"ssafctsoy.4.161.35.151.nip.io",
			"our-forum.bseit-wp-hheo.local",
		];
		const currentDomain = window.location.hostname;

		// Force staging for specific domains
		if (forceStagingDomains.includes(currentDomain)) {
			return Constants.APIS.ROOT_URL.STG;
		}

		switch (envType) {
			case "dev":
				return Constants.APIS.ROOT_URL.DEV;
			case "qc":
				return Constants.APIS.ROOT_URL.QC;
			case "stg":
				return Constants.APIS.ROOT_URL.STG;
			case "prod":
				return Constants.APIS.ROOT_URL.PROD;
			default:
				return Constants.APIS.ROOT_URL.DEV;
		}
	}

	/**
	 * Returns the API root URL for making API calls
	 *
	 * @return {string} The API root URL
	 */
	getApiUrl() {
		return this.getApiRootUrl();
	}

	/**
	 * Generic fetch function for paginated and filtered data
	 *
	 * @param {string} apiName - API identifier (e.g., 'JOBS', 'VOLUNTEER')
	 * @param {string} endpoint - API endpoint (e.g., 'list', 'types')
	 * @param {Object} params - Query parameters
	 * @return {Promise} API response
	 */
	async fetchExternalApiData(apiUrl, params = {}) {
		// Build query string only from non-empty parameters
		const queryParams = new URLSearchParams();
		Object.entries(params).forEach(([key, value]) => {
			if (value !== undefined && value !== null && value !== "") {
				queryParams.append(key, value);
			}
		});

		const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
		// console.log(`${apiUrl}/${queryString}`);

		const response = await fetch(`${apiUrl}/${queryString}`, {
			method: "GET",
			headers: {
				"Accept-Language": document.documentElement.lang,
			},
		});

		if (!response.ok) {
			throw new Error(`Error: ${response.status}`);
		}

		return await response.json();
	}

	/**
	 * Makes a POST request to the specified API endpoint
	 *
	 * @param {string} endpoint - API endpoint path
	 * @param {Object} data - Data to send in the request body
	 * @return {Promise<Object>} API response
	 */
	async post(endpoint, data = {}) {
		try {
			const apiUrl = `${this.getApiUrl()}${endpoint}`;

			const response = await fetch(apiUrl, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"Accept-Language": document.documentElement.lang,
				},
				body: JSON.stringify(data),
			});

			const responseData = await response.json();

			if (!response.ok) {
				return {
					success: false,
					message: responseData.message || `Error: ${response.status}`,
					data: responseData,
				};
			}

			return {
				success: true,
				message: responseData.message || "Success",
				data: responseData.data,
			};
		} catch (error) {
			console.error("API POST error:", error);
			return {
				success: false,
				message: error.message || "An unexpected error occurred",
				error,
			};
		}
	}

	/**
	 * Makes a PUT request to the specified API endpoint
	 *
	 * @param {string} endpoint - API endpoint to call
	 * @param {Object} data - Data to send in the request body
	 * @return {Promise<Object>} API response
	 */
	async put(endpoint, data = {}) {
		try {
			const apiUrl = `${this.getApiUrl()}${endpoint}`;

			const response = await fetch(apiUrl, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					"Accept-Language": document.documentElement.lang,
				},
				body: JSON.stringify(data),
			});

			const responseData = await response.json();

			if (!response.ok) {
				return {
					success: false,
					message: responseData.message || `Error: ${response.status}`,
					data: responseData,
				};
			}

			return {
				success: true,
				message: responseData.message || "Success",
				data: responseData.data,
			};
		} catch (error) {
			console.error("API PUT error:", error);
			return {
				success: false,
				message: error.message || "An unexpected error occurred",
				error,
			};
		}
	}

	async fetchJobsList(filtersData, pageIndex, pageSize, entityCode, callbacks) {
		const { setLoading, setError, setJobs, setTotalPages, setPageIndex } =
			callbacks;
		const jobsListApiUrl = `${this.getApiRootUrl()}${
			Constants.APIS.JOBS.LIST
		}/${entityCode}`;

		setLoading(true);
		setError(null);

		try {
			const jobsResponse = await this.fetchExternalApiData(jobsListApiUrl, {
				...filtersData,
				pageIndex,
				pageSize,
			});

			if (jobsResponse.data && jobsResponse.data.count > 0) {
				setJobs(jobsResponse.data.data || []);
				setTotalPages(jobsResponse.data.totalPages);
				setPageIndex(jobsResponse.data.page);
			} else {
				throw new Error("Something went wrong!");
			}
		} catch (error) {
			console.error("Error fetching jobs:", error);
			setError(error.message || "An unexpected error occurred");
		} finally {
			setLoading(false);
		}
	}
}
export default new BSHelper();
